import React, { useMemo, useState, useCallback, useEffect } from 'react';
// import { useSignals } from '@preact/signals-react/runtime';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import translation from '../../signals/translations';
import useData from '../../hooks/useData';
import IconClose from '../../icons/IconClose';
import IconHeart from '../../icons/IconHeart';
import IconHeartFilled from '../../icons/IconHeartFilled';
import IconArrowDown from '../../icons/IconArrowDown';
import SlideToggle from '../SlideToggle';
import useRecord from '../../hooks/useRecord';

const SearchPanel = () => {
	// useSignals();

	const { exhibitorInfo } = jsonData.value;
	const { SearchPanel } = appState.value;
	const { toggleFavourite } = useData();
	const [searchTerm, setSearchTerm] = useState('');
	const [showSearchResult, setShowSearchResult] = useState(false);
	const [categories, setCategories] = useState([]);
	const { makeRecord } = useRecord();
	const [searchType, setSearchType] = useState('company');

	const categoryList = useMemo(() => {
		const list = [];

		exhibitorInfo.forEach((exhibitor) => {
			exhibitor.products.forEach((product) => {
				const categoryLevel1 = product['產品大分類'];
				const categoryLevel2 = product['產品中分類'];
				const categoryLevel3 = product['產品小分類'];

				if (!list.find((category) => category.name === categoryLevel1)) {
					list.push({
						name: categoryLevel1,
						level2: [],
					});
				}

				if (!list.find((category) => category.name === categoryLevel1).level2.find((level2) => level2.name === categoryLevel2)) {
					list.find((category) => category.name === categoryLevel1).level2.push({
						name: categoryLevel2,
						active: true,
						level3: [],
					});
				}

				if (!list.find((category) => category.name === categoryLevel1).level2.find((level2) => level2.name === categoryLevel2).level3.find((level3) => level3.name === categoryLevel3)) {
					list.find((category) => category.name === categoryLevel1).level2.find((level2) => level2.name === categoryLevel2).level3.push({
						name: categoryLevel3,
						active: true,
					});
				}
			});
		});

		list.forEach((category, index) => {
			if (!category.name) {
				list.splice(index, 1);
			}
		});

		list.forEach((category) => {
			category.level2.forEach((level2, index) => {
				if (!level2.name) {
					category.level2.splice(index, 1);
				}
			});
		});

		list.forEach((category) => {
			category.level2.forEach((level2) => {
				level2.level3.forEach((level3, index) => {
					if (!level3.name) {
						level2.level3.splice(index, 1);
					}
				});
			});
		});

		return list;
	}, [exhibitorInfo]);

	useEffect(() => {
		setCategories(categoryList);
	}, [categoryList]);

	const toggleLevel2Check = useCallback(({ categoryIndex, level2Index }) => {
		const updatedCategories = [...categories];

		updatedCategories[categoryIndex].level2[level2Index].active = !updatedCategories[categoryIndex].level2[level2Index].active;

		updatedCategories[categoryIndex].level2[level2Index].level3.forEach((level3) => {
			level3.active = updatedCategories[categoryIndex].level2[level2Index].active;
		});

		setCategories(updatedCategories);
	}, [categories]);

	const toggleLevel3Check = useCallback(({ categoryIndex, level2Index, level3Index }) => {
		const updatedCategories = [...categories];

		updatedCategories[categoryIndex].level2[level2Index].level3[level3Index].active = !updatedCategories[categoryIndex].level2[level2Index].level3[level3Index].active;

		updatedCategories[categoryIndex].level2[level2Index].active = updatedCategories[categoryIndex].level2[level2Index].level3.some((level3) => level3.active);

		setCategories(updatedCategories);
	}, [categories]);

	const isLevel1CategoriesOnly = useMemo(() => {
		return categories.every((category) => {
			return !category.level2.length;
		});
	}, [categories]);

	const exhibitorsContainingSelectedCategories = useMemo(() => {
		const exhibitors = [];

		if (isLevel1CategoriesOnly) {
			return exhibitorInfo;
		}

		exhibitorInfo.forEach((exhibitor) => {
			let hasActiveProduct = false;
			exhibitor.products.forEach((product) => {
				if (
					categories.some((category) => {
						return category.level2.some((level2) => {
							if (level2.active && !level2.level3.length) return true;
							if (level2.active) {
								return level2.level3.some((level3) => {
									return level3.active && (level3.name === product['產品小分類']);
								}) && (level2.name === product['產品中分類']);
							}
							return false;
						}) && (category.name === product['產品大分類']);
					})
				) {
					hasActiveProduct = true;
				}
			});

			if (hasActiveProduct) exhibitors.push(exhibitor);
		});

		return exhibitors;
	}, [isLevel1CategoriesOnly, exhibitorInfo, categories]);

	const filteredExhibitorInfo = useMemo(() => {
		if (searchType === 'product') {
			if (!searchTerm) return exhibitorsContainingSelectedCategories;
			if (searchTerm) return exhibitorsContainingSelectedCategories.filter((exhibitor) => {
				const term = searchTerm.toLowerCase();
				return (exhibitor['廠商名稱'].toLowerCase().includes(term) || exhibitor['攤位號碼'].toLowerCase().includes(term));
			});
		}

		if (searchType === 'company') {
			if (!searchTerm) return exhibitorInfo;
			if (searchTerm) return exhibitorInfo.filter((exhibitor) => {
				const term = searchTerm.toLowerCase();
				return (exhibitor['廠商名稱'].toLowerCase().includes(term) || exhibitor['攤位號碼'].toLowerCase().includes(term));
			});
		}
	}, [exhibitorInfo, exhibitorsContainingSelectedCategories, searchTerm, searchType]);

	const productsContainingSelectedCategories = useMemo(() => {
		const products = [];

		if (isLevel1CategoriesOnly) {
			return exhibitorInfo.reduce((acc, exhibitor) => {
				exhibitor.products.forEach((product) => {
					acc.push(product);
				});
				return acc;
			}, []);
		}

		exhibitorInfo.forEach((exhibitor) => {
			exhibitor.products.forEach((product) => {
				if (
					categories.some((category) => {
						return category.level2.some((level2) => {
							if (level2.active && !level2.level3.length) return true;
							if (level2.active) {
								return level2.level3.some((level3) => {
									return level3.active && (level3.name === product['產品小分類']);
								}) && (level2.name === product['產品中分類']);
							}
							return false;
						}) && (category.name === product['產品大分類']);
					})
				) {
					products.push(product);
				}
			});
		});

		return products;
	}, [isLevel1CategoriesOnly, exhibitorInfo, categories]);

	const filteredProductInfo = useMemo(() => {
		if (!searchTerm) return productsContainingSelectedCategories;
		if (searchTerm) return productsContainingSelectedCategories.filter((product) => {
			const term = searchTerm.toLowerCase();
			return product['產品名稱'].includes(term);
		});
	}, [productsContainingSelectedCategories, searchTerm]);

	const closePanel = () => {
		appState.value = {
			...appState.value,
			ExhibitorList: false,
			MyFavourites: false,
			SearchPanel: false,
		};
	};

	const toggleFavouriteExhibitor = useCallback((boothNumber) => {
		toggleFavourite(boothNumber);
	}, [toggleFavourite]);

	const chooseExhibitor = (boothNumber, floor) => {
		appState.value = {
			...appState.value,
			ExhibitorInfo: true,
			activeFloor: floor !== undefined ? floor : appState.value.activeFloor,
			selectedBooth: boothNumber,
			selectedExhibitor: boothNumber,
		};
	};

	const recordQuery = useCallback(() => {
		if (showSearchResult) return;

		return makeRecord({
			Action: 'S',
			Keyword: searchTerm,
		});
	}, [makeRecord, searchTerm, showSearchResult]);

	const toggleQuery = useCallback(() => {
		setShowSearchResult(!showSearchResult);
		recordQuery();
	}, [recordQuery, showSearchResult]);

	const chooseProduct = (relatedExhibitor, index) => {
		const product = relatedExhibitor.products[index];

		appState.value = {
			...appState.value,
			ExhibitorInfo: true,
			activeFloor: relatedExhibitor.floor,
			selectedBooth: relatedExhibitor['攤位號碼'],
			selectedExhibitor: relatedExhibitor['攤位號碼'],
			selectedProduct: product,
		};
	};

	const selectAllCategories = useCallback(() => {
		const updatedCategories = [...categories];

		updatedCategories.forEach((category) => {
			category.level2.forEach((level2) => {
				level2.active = true;
				level2.level3.forEach((level3) => {
					level3.active = true;
				});
			});
		});

		setCategories(updatedCategories);
	}, [categories]);

	const clearAllCategories = useCallback(() => {
		const updatedCategories = [...categories];

		updatedCategories.forEach((category) => {
			category.level2.forEach((level2) => {
				level2.active = false;
				level2.level3.forEach((level3) => {
					level3.active = false;
				});
			});
		});

		setCategories(updatedCategories);
	}, [categories]);

	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const recordProductClicked = useCallback((product, booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
			Product: product['產品名稱'],
		});
	}, [makeRecord]);

	if (!SearchPanel) return null;

	return (
		<div id='SearchPanel' className={`ui-panel booth ${SearchPanel ? 'active' : ''}`}>
			<div className='panel-heading'>{translation('leftPanel.advancedSearch')}</div>
			<div className='panel-body'>
				<div className='flex-controls'>
					<div className='search'>
						<input type='text' placeholder={searchType === 'company' ? translation('leftPanel.advancedSearch.searchExhibitor') : translation('leftPanel.advancedSearch.searchProduct')} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value) }} />
					</div>
					<div className='search-type'>
						<select value={searchType} onChange={(event) => {
							setSearchType(event.target.value);
						}}>
							<option value='company'>{translation('leftPanel.advancedSearch.exhibitor')}</option>
							<option value='product'>{translation('leftPanel.advancedSearch.product')}</option>
						</select>
					</div>
				</div>

				<div className='search-button' onClick={toggleQuery}>{showSearchResult ? (translation('leftPanel.advancedSearch.restart')) : (translation('leftPanel.advancedSearch.submit'))}</div>

				{searchType === 'company' && showSearchResult ? (
					<div className='exhibitors'>
						{filteredExhibitorInfo.map((exhibitor, index) => {
							const exhibitorName = exhibitor['廠商名稱'];
							const booth = exhibitor['攤位號碼'];

							return (
								<div className='exhibitor' key={`exhibitorLinkTableRow${index}`}>
									<div className='name' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{exhibitorName}</div>
									<div className='booth-number' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{booth}</div>
									<div className='icon' onClick={() => { toggleFavouriteExhibitor(booth) }}>
										{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}
									</div>
								</div>
							)
						})}
					</div>
				) : null}

				{searchType === 'company' && showSearchResult && !filteredExhibitorInfo.length ? (
					<div className='no-result'>{translation('leftPanel.advancedSearch.noResult')}</div>
				) : null}

				{searchType === 'product' && showSearchResult ? (
					<div className='products'>
						{filteredProductInfo.map((product, i) => {
							const exhibitor = exhibitorInfo.find((exhibitor) => exhibitor['攤位號碼'] === product['攤位號碼']);

							return (
								<div className='product-wrap' key={`product-${i}`} onClick={() => { chooseProduct(exhibitor, i, product); recordProductClicked(product, exhibitor['攤位號碼']); }}>
									<div className='product-image' style={{ backgroundImage: `url(./product-photos/${product['產品圖']})` }}></div>
									<div className='product-name'>{product['產品名稱']}</div>
								</div>
							)
						})}
					</div>
				) : null}

				{searchType === 'product' && showSearchResult && !filteredProductInfo.length ? (
					<div className='no-result'>{translation('leftPanel.advancedSearch.noResult')}</div>
				) : null}

				{!showSearchResult ? (
					<div>
						<div className='flex-end'>
							<div className='button' onClick={selectAllCategories}>{translation('leftPanel.advancedSearch.selectAll')}</div>
							<div className='button' onClick={clearAllCategories}>{translation('leftPanel.advancedSearch.clearAll')}</div>
						</div>

						{categories.map((category, index) => {
							return (
								<div className='category' key={`category${index}`}>

									<div className='name-level1'>{category.name}</div>
									<div className='level2'>
										{category.level2.map((level2, inde) => {
											return (
												<SlideToggle collapsed key={`level2${inde}`}>
													{({ toggle, setCollapsibleElement }) => (
														<div className='level2-item'>
															<div className='flex-control'>
																<div className='check-wrap' onClick={() => {
																	toggleLevel2Check({
																		categoryIndex: index,
																		level2Index: inde,
																	});
																}}>
																	<div className={`checkbox ${level2.active ? 'active' : ''}`}></div>
																	<div className='name'>{level2.name}</div>
																</div>
																<IconArrowDown onClick={toggle} />
															</div>

															<div className='level3' ref={setCollapsibleElement}>
																{level2.level3.map((level3, ind) => {
																	return (
																		<div className='level3-item' key={`level3${ind}`}>
																			<div className='check-wrap' onClick={() => {
																				toggleLevel3Check({
																					categoryIndex: index,
																					level2Index: inde,
																					level3Index: ind,
																				})
																			}}>
																				<div className={`checkbox ${level3.active ? 'active' : ''}`}></div>
																				<div className='name'>{level3.name}</div>
																			</div>
																		</div>
																	)
																})}
																<div className='pad'></div>
															</div>
														</div>
													)}
												</SlideToggle>
											)
										})}
									</div>
								</div>
							)
						})}
					</div>
				) : null}

			</div>

			<IconClose onClick={closePanel} />
		</div>
	)
}

export default SearchPanel;
